/**
 * Query Statistics Types
 *
 * Types for database-level statistics returned by the /stats endpoint.
 * These are used to display column metadata, demographics, and clinical
 * summaries in the Stats tab of the TableEditorPane.
 */

export type ColumnStats = {
    column_name: string;
    data_type: 'numeric' | 'text' | 'date' | 'boolean' | 'other';
    null_count: number;
    distinct_count: number;
    // Numeric columns
    min?: number;
    max?: number;
    avg?: number;
    // Date columns
    min_date?: string;
    max_date?: string;
    // Categorical/text columns (top 5 values with counts)
    top_values?: Array<{ value: string; count: number }>;
};

export type ClinicalDomainStats = {
    total_distinct: number;
    top_items: Array<{
        concept_name: string;
        person_count: number;
        icd_code?: string;
    }>;
    date_range?: { min_date?: string; max_date?: string };
};

export type ClinicalSummary = {
    conditions?: ClinicalDomainStats;
    medications?: ClinicalDomainStats;
    procedures?: ClinicalDomainStats;
    visits?: {
        total_count: number;
        avg_per_patient?: number;
        date_range?: { min_date?: string; max_date?: string };
    };
};

export type ResultStats = {
    total_row_count: number;
    column_count: number;
    columns: ColumnStats[];
    // Demographic distribution when person_id is present
    demographics?: {
        age_distribution?: Array<{ range: string; count: number }>;
        gender_distribution?: Array<{ value: string; count: number }>;
    };
    // Clinical summary when person_id is present
    clinical_summary?: ClinicalSummary;
    // Partial stats indicator (some queries failed but others succeeded)
    partial?: boolean;
    // Warning messages for non-fatal issues
    warnings?: string[];
};
